if (!customElements.get('dd-commerce-progress')) {
  customElements.define(
    'dd-commerce-progress',
    class DDCommerceProgress extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ddBound === 'true') return;
        this.dataset.ddBound = 'true';
        this.productForm = this.closest('dd-pdp-product-form');
        this.handleSelectionChange = this.handleSelectionChange.bind(this);
        this.productForm?.addEventListener('dd:pdp-selection-change', this.handleSelectionChange);
        this.render();
      }

      disconnectedCallback() {
        this.productForm?.removeEventListener('dd:pdp-selection-change', this.handleSelectionChange);
      }

      handleSelectionChange(event) {
        const lines = Array.isArray(event.detail?.lines) ? event.detail.lines : [];
        this.selectedSubtotal = this.number(event.detail?.subtotal);
        this.selectedQualifyingCount = lines.reduce(
          (total, line) => total + (line.available && line.mixMatchEligible ? this.number(line.quantity) : 0),
          0
        );
        this.render();
      }

      render() {
        const shippingThreshold = this.number(this.dataset.shippingThreshold);
        const projectedSubtotal =
          this.number(this.dataset.cartSubtotal) +
          (this.selectedSubtotal ?? this.number(this.dataset.selectedSubtotal));
        const shippingRemaining = Math.max(0, shippingThreshold - projectedSubtotal);
        const shippingProgress = shippingThreshold > 0 ? Math.min(100, (projectedSubtotal / shippingThreshold) * 100) : 100;
        const shippingStatus = this.querySelector('[data-dd-shipping-status]');
        const shippingTrack = this.querySelector('[data-dd-shipping-progress]');

        if (shippingStatus) {
          shippingStatus.textContent = shippingRemaining
            ? this.template(this.dataset.shippingRemainingTemplate, {
                amount: this.formatMoney(shippingRemaining),
              })
            : this.dataset.shippingUnlockedTemplate;
        }
        this.updateTrack(shippingTrack, projectedSubtotal, shippingThreshold, shippingProgress);

        const mixModule = this.querySelector('[data-dd-mix-match-module]');
        const mixConfigured = this.dataset.mixMatchConfigured === 'true';
        const mixThreshold = Math.max(1, this.number(this.dataset.mixMatchThreshold));
        const mixPercent = this.number(this.dataset.mixMatchPercent);
        const qualifyingCount =
          this.number(this.dataset.cartQualifyingCount) +
          (this.selectedQualifyingCount ?? this.number(this.dataset.selectedQualifyingCount));

        if (!mixModule) return;
        mixModule.hidden = !mixConfigured || qualifyingCount === 0;
        if (mixModule.hidden) return;

        const mixRemaining = Math.max(0, mixThreshold - qualifyingCount);
        const mixConfirmed = this.dataset.mixMatchConfirmed === 'true';
        const mixStatus = this.querySelector('[data-dd-mix-match-status]');
        const mixCounter = this.querySelector('[data-dd-mix-match-counter]');
        const mixTrack = this.querySelector('[data-dd-mix-match-progress]');

        if (mixStatus) {
          mixStatus.textContent = mixConfirmed
            ? this.template(this.dataset.mixConfirmedTemplate, { percent: mixPercent })
            : mixRemaining === 0
              ? this.template(this.dataset.mixQualifiedTemplate, { percent: mixPercent })
              : this.template(this.dataset.mixRemainingTemplate, {
                  count: mixRemaining,
                  percent: mixPercent,
                });
        }
        if (mixCounter) {
          mixCounter.textContent = this.template(this.dataset.mixProgressTemplate, {
            count: Math.min(qualifyingCount, mixThreshold),
            threshold: mixThreshold,
          });
        }
        const isCart = this.dataset.context === 'cart';
        const secondMilestone = Math.max(1, mixThreshold - 1);
        const mixProgress = isCart
          ? qualifyingCount >= mixThreshold
            ? 100
            : qualifyingCount >= secondMilestone
              ? 50
              : 0
          : Math.min(100, (qualifyingCount / mixThreshold) * 100);
        this.updateTrack(mixTrack, qualifyingCount, mixThreshold, mixProgress);

        if (isCart) {
          this.querySelectorAll('[data-dd-mix-match-milestone]').forEach((milestone) => {
            const milestoneValue = this.number(milestone.dataset.ddMixMatchMilestone);
            milestone.classList.toggle('is-reached', qualifyingCount >= milestoneValue);
            milestone.classList.toggle('is-confirmed', milestoneValue === mixThreshold && mixConfirmed);
          });
        }
      }

      updateTrack(track, value, maximum, percentage) {
        if (!track) return;
        track.setAttribute('aria-valuenow', String(Math.min(value, maximum)));
        track.querySelector('.dd-commerce-progress__fill')?.style.setProperty('--dd-commerce-progress', `${percentage}%`);
      }

      template(value = '', replacements = {}) {
        return Object.entries(replacements).reduce(
          (output, [key, replacement]) => output.replaceAll(`__${key}__`, String(replacement)),
          value
        );
      }

      formatMoney(cents) {
        const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency,
          }).format(cents / 100);
        } catch (_error) {
          return `$${(cents / 100).toFixed(2)}`;
        }
      }

      number(value, fallback = 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      }
    }
  );
}
